import { thousandSeperatorWithPrefix } from 'utils'

import type { SuspenseAfterRefund } from './payoffRefund'

/**
 * Where the payoff's overage ends up, shown under the Other Payments lines that hand it back.
 *
 * Smartsheet row 548 (lbetty, 11/02): *"can we have a final total at the bottom once we add the
 * refund that would show the final total of the suspense account... we would like to make sure
 * that keying errors doesn't allow us to refund the incorrect amount."* The overage and the refund
 * are keyed in two different places on this screen, the Suspense field at the top and the payee
 * and amount at the bottom, and nothing added them up.
 *
 * Its own component so the figures a servicer reads are rendered by a test rather than only
 * computed by one: the arithmetic is `suspenseAfterRefund`, and this is the half of it that can be
 * wrong without any number being wrong (a line omitted, a sign flipped in the label).
 */
export const PayoffSuspenseSummary = ({ roll }: { roll: SuspenseAfterRefund }) => {
  /**
   * Red is kept for the closing balance going negative, which is the only line that means
   * something is wrong. The draw and the refund are negative by definition, and colouring them
   * made a correct payoff that nets to zero read as three errors.
   */
  const line = (label: string, value: number, { strong = false, danger = false } = {}) => (
    <div className={`flex items-baseline justify-between gap-4 ${strong ? 'font-semibold' : ''}`} key={label}>
      <span className={strong ? '' : 'text-gray-500'}>{label}</span>
      <span className={danger ? 'text-red-600' : ''}>{thousandSeperatorWithPrefix(value)}</span>
    </div>
  )

  return (
    <div className="mt-3 border-t border-slate-200 pt-2">
      <p className="font-semibold text-sm mb-1">Suspense after this payoff</p>
      <div className="flex flex-col gap-1 text-sm">
        {roll.opening !== 0 && line('Already in suspense on this loan', roll.opening)}
        {line('Held on this payment', roll.credited)}
        {roll.applied !== 0 && line('Applied from suspense to this payoff', -roll.applied)}
        {roll.refunded !== 0 && line('Refund to the borrower', -roll.refunded)}
        {line(
          roll.refunded !== 0 ? 'Left in suspense once the refund is paid' : 'Left in suspense',
          roll.closing,
          { strong: true, danger: roll.closing < 0 },
        )}
      </div>
      {/* The one keying error that does more than leave a wrong balance behind, so it says what
          will actually happen rather than just flagging the figure. */}
      {roll.overRefunded > 0 && (
        <p className="mt-2 text-xs leading-snug text-red-600">
          {thousandSeperatorWithPrefix(roll.overRefunded)} more than this payment put into suspense. Paid as it stands,
          the refund is booked against the Construction Reserve instead of suspense and the overage stays in suspense:
          correct the amount before applying.
        </p>
      )}
      {roll.overRefunded === 0 && roll.refunded > 0 && (
        <p className="mt-2 text-xs leading-snug text-gray-500">
          Paid out of the suspense this payment holds. The balance above is what suspense reads once the refund is paid
          out of the check queue, not today.
        </p>
      )}
    </div>
  )
}
